import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Camera, Upload, Trash2, X, ChevronLeft, ChevronRight, ImagePlus, Star, StarOff } from "lucide-react";

const API = "/api";

/**
 * Photo gallery for an event.
 * - Public-to-logged-in: thumbnail grid + lightbox with prev/next
 * - Admin-only: multi-file upload + delete + set cover photo
 */
export default function EventPhotos({ eventId, onCoverChanged }) {
  const { isAdmin, getAuthHeader } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState([]); // FileList -> array
  const [lightboxIndex, setLightboxIndex] = useState(-1);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const fileInputRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const headers = getAuthHeader();
      const res = await axios.get(`${API}/events/${eventId}/photos?t=${Date.now()}`, { headers });
      setPhotos(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Failed to load photos", e);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [eventId, getAuthHeader]);

  useEffect(() => {
    if (eventId) reload();
  }, [eventId, reload]);

  // Keyboard navigation in lightbox
  useEffect(() => {
    if (lightboxIndex < 0) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIndex(-1);
      else if (e.key === "ArrowRight") setLightboxIndex((i) => (i + 1) % photos.length);
      else if (e.key === "ArrowLeft") setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxIndex, photos.length]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    setSelected(files);
  };

  const submitUpload = async () => {
    if (!selected.length) {
      toast.error("Choose at least one photo to upload.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    selected.forEach((f) => fd.append("photos[]", f));
    try {
      const res = await axios.post(`${API}/events/${eventId}/photos`, fd, {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "Photos uploaded");
      if (res.data?.errors?.length) {
        toast.warning(`Some files were skipped: ${res.data.errors.join("; ")}`);
      }
      setSelected([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setUploadOpen(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async (photo) => {
    try {
      await axios.delete(`${API}/events/${eventId}/photos/${photo.id}`, {
        headers: getAuthHeader(),
      });
      toast.success("Photo deleted");
      setConfirmDelete(null);
      setLightboxIndex(-1);
      reload();
      if (photo.is_cover && typeof onCoverChanged === "function") onCoverChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete photo");
    }
  };

  const toggleCover = async (photo) => {
    const willBeCover = !photo.is_cover;
    try {
      await axios.patch(
        `${API}/events/${eventId}/photos/${photo.id}/cover`,
        { is_cover: willBeCover },
        { headers: getAuthHeader() }
      );
      toast.success(willBeCover ? "Cover photo set" : "Cover photo cleared");
      reload();
      if (typeof onCoverChanged === "function") onCoverChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update cover");
    }
  };

  const current = lightboxIndex >= 0 ? photos[lightboxIndex] : null;

  return (
    <Card data-testid="event-photos-card">
      <CardHeader className="flex flex-row items-center justify-between gap-3">
        <CardTitle className="font-serif text-xl flex items-center gap-2">
          <Camera className="text-[#1B3B6F]" />
          Photo Gallery
          {photos.length > 0 && (
            <span className="text-sm font-normal text-slate-500">({photos.length})</span>
          )}
        </CardTitle>
        {isAdmin() && (
          <Button
            size="sm"
            onClick={() => setUploadOpen(true)}
            data-testid="event-photos-upload-btn"
          >
            <ImagePlus size={14} className="mr-1" /> Add Photos
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-slate-500 text-sm">Loading photos…</p>
        ) : photos.length === 0 ? (
          <p className="text-slate-500 italic text-sm">
            No photos yet for this event.
            {isAdmin() && " Click 'Add Photos' to upload."}
          </p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {photos.map((p, idx) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setLightboxIndex(idx)}
                className="group relative aspect-square overflow-hidden rounded-lg border border-slate-200 bg-slate-100 hover:ring-2 hover:ring-[#1B3B6F] transition"
                data-testid={`event-photo-thumb-${idx}`}
                title={p.title || ""}
              >
                <img
                  src={p.file_path}
                  alt={p.title || "Event photo"}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
                />
                {p.is_cover ? (
                  <div
                    className="absolute top-1.5 left-1.5 bg-amber-400 text-amber-900 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full flex items-center gap-1 shadow"
                    data-testid={`event-photo-cover-badge-${idx}`}
                  >
                    <Star size={10} className="fill-amber-900" /> Cover
                  </div>
                ) : null}
                {p.title && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5">
                    <p className="text-[11px] text-white truncate">{p.title}</p>
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </CardContent>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onOpenChange={(o) => !uploading && setUploadOpen(o)}>
        <DialogContent data-testid="event-photos-upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload size={18} /> Upload Event Photos
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <label
              htmlFor="event-photo-file-input"
              className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-lg p-6 cursor-pointer hover:bg-slate-50"
            >
              <ImagePlus className="text-slate-400 mb-2" />
              <p className="text-sm text-slate-600 text-center">
                <span className="text-[#1B3B6F] font-semibold">Choose photos</span> or drag &amp; drop
              </p>
              <p className="text-xs text-slate-500 mt-1">JPG, PNG, GIF, WebP · 10 MB max each</p>
              <input
                id="event-photo-file-input"
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                className="hidden"
                onChange={onPick}
                data-testid="event-photos-file-input"
              />
            </label>
            {selected.length > 0 && (
              <div className="text-xs text-slate-600">
                <p className="font-medium mb-1">{selected.length} file(s) selected:</p>
                <ul className="max-h-32 overflow-y-auto space-y-0.5 pl-3 list-disc">
                  {selected.map((f) => (
                    <li key={f.name + f.size} className="truncate">
                      {f.name} <span className="text-slate-400">({(f.size / 1024).toFixed(0)} KB)</span>
                    </li>
                  ))}
                </ul>
                <p className="text-slate-500 mt-2">
                  Each photo's title will be set to its filename. You can rename via the database if needed.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setUploadOpen(false)}
              disabled={uploading}
              data-testid="event-photos-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={submitUpload}
              disabled={uploading || selected.length === 0}
              data-testid="event-photos-submit-btn"
            >
              <Upload size={14} className="mr-1" />
              {uploading ? "Uploading…" : `Upload ${selected.length || ""}`.trim()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {current && (
        <div
          className="fixed inset-0 z-[60] bg-black/85 flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(-1)}
          data-testid="event-photos-lightbox"
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setLightboxIndex(-1);
            }}
            className="absolute top-4 right-4 text-white/90 hover:text-white p-2"
            aria-label="Close"
            data-testid="event-photos-lightbox-close"
          >
            <X size={28} />
          </button>
          {photos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((i) => (i - 1 + photos.length) % photos.length);
                }}
                className="absolute left-2 sm:left-6 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
                aria-label="Previous"
                data-testid="event-photos-lightbox-prev"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((i) => (i + 1) % photos.length);
                }}
                className="absolute right-2 sm:right-6 text-white/80 hover:text-white p-2 bg-black/30 rounded-full"
                aria-label="Next"
                data-testid="event-photos-lightbox-next"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}
          <div
            className="max-w-5xl max-h-[85vh] flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={current.file_path}
              alt={current.title || "Event photo"}
              className="max-h-[78vh] max-w-full object-contain rounded shadow-2xl"
            />
            <div className="mt-3 flex items-center gap-3 text-white/90 text-sm">
              <span className="truncate max-w-[60vw]">{current.title || current.filename}</span>
              <span className="text-white/50">·</span>
              <span className="text-white/60">
                {lightboxIndex + 1} / {photos.length}
              </span>
              {isAdmin() && (
                <>
                  <Button
                    size="sm"
                    variant={current.is_cover ? "secondary" : "default"}
                    onClick={() => toggleCover(current)}
                    data-testid="event-photos-lightbox-cover"
                    title={current.is_cover ? "Remove as cover photo" : "Set as cover photo"}
                  >
                    {current.is_cover ? (
                      <>
                        <StarOff size={14} className="mr-1" /> Unset Cover
                      </>
                    ) : (
                      <>
                        <Star size={14} className="mr-1" /> Set as Cover
                      </>
                    )}
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => setConfirmDelete(current)}
                    data-testid="event-photos-lightbox-delete"
                  >
                    <Trash2 size={14} className="mr-1" /> Delete
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="event-photos-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the photo from the gallery. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => confirmDelete && doDelete(confirmDelete)}
              className="bg-red-600 hover:bg-red-700"
              data-testid="event-photos-delete-confirm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
